/**
 * JsonPptRenderer — PPT 幻灯片自动排版与演讲引擎
 *
 * 接收大模型通过 Zod Tool 输出的结构化 PPT JSON，
 * 在前端以 16:9 轮播卡片形态展现幻灯片内容，
 * 底部附带 speakerNotes（演讲者备注），供律师/汇报者直接阅读。
 */

import React, { useState } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';

// ── 类型定义 ──
export interface PptSlide {
  layout?: string;         // 'TitleAndContent' | 'TitleOnly' | 'Blank' ...
  title: string;
  subtitle?: string;
  bullets?: string[];
  speakerNotes?: string;
  imageUrl?: string;       // 未来扩展：幻灯片配图
}

export interface PptDocumentJson {
  fileName: string;
  slides: PptSlide[];
}

// ── 单页幻灯片卡片 ──
const SlideCard: React.FC<{ slide: PptSlide; index: number; total: number }> = ({ slide, index, total }) => {
  return (
    <div
      className="relative w-full bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 rounded-lg overflow-hidden"
      style={{ aspectRatio: '16 / 9' }}
    >
      {/* 页码 */}
      <div className="absolute top-3 right-4 text-xs text-gray-500 font-mono">
        {index + 1} / {total}
      </div>

      {/* 内容区 */}
      <div className="flex flex-col justify-center items-center h-full px-12 py-8 text-white">
        {/* 标题 */}
        <h2
          className="text-xl md:text-2xl font-bold text-center mb-2 leading-tight"
          style={{ fontFamily: '"Inter", "Helvetica Neue", Arial, sans-serif' }}
        >
          {slide.title}
        </h2>

        {/* 副标题 */}
        {slide.subtitle && (
          <p className="text-sm text-gray-400 text-center mb-4">{slide.subtitle}</p>
        )}

        {/* 要点列表 */}
        {slide.bullets && slide.bullets.length > 0 && (
          <ul className="mt-4 space-y-2 text-left max-w-lg w-full">
            {slide.bullets.map((bullet, i) => (
              <li key={i} className="flex items-start gap-3 text-sm md:text-base text-gray-200">
                <span className="mt-1 w-2 h-2 rounded-full bg-blue-400 flex-shrink-0" />
                <span>{bullet}</span>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
};

// ── 主组件 ──
interface JsonPptRendererProps {
  document: PptDocumentJson;
  onExport?: () => void;
}

const JsonPptRenderer: React.FC<JsonPptRendererProps> = ({ document, onExport }) => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const slides = document.slides;
  const slide = slides[currentSlide];

  if (!slides || slides.length === 0) {
    return <div className="p-4 text-sm text-gray-400">Empty presentation</div>;
  }

  return (
    <div className="my-4 rounded-lg overflow-hidden border border-gray-700 shadow-lg bg-gray-950">
      {/* 顶部标题栏 */}
      <div className="flex items-center justify-between px-4 py-2 bg-gray-900 border-b border-gray-700">
        <div className="flex items-center gap-2">
          <span className="text-lg">📽️</span>
          <span className="text-sm font-semibold text-gray-300 truncate max-w-[240px]">{document.fileName}</span>
        </div>
        {onExport && (
          <button
            onClick={onExport}
            className="flex items-center gap-1 px-3 py-1 text-xs font-medium text-orange-300 bg-orange-950 border border-orange-800 rounded-md hover:bg-orange-900 transition-colors"
          >
            ⬇️ 导出 PPTX
          </button>
        )}
      </div>

      {/* 幻灯片卡片 */}
      <div className="relative px-4 pt-4 pb-2">
        <SlideCard slide={slide} index={currentSlide} total={slides.length} />

        {/* 左右控制箭头 */}
        {slides.length > 1 && (
          <>
            <button
              onClick={() => setCurrentSlide(Math.max(0, currentSlide - 1))}
              disabled={currentSlide === 0}
              className="absolute left-1 top-1/2 -translate-y-1/2 w-8 h-8 flex items-center justify-center rounded-full bg-black/40 text-white hover:bg-black/60 disabled:opacity-20 disabled:cursor-not-allowed transition-all"
            >
              <ChevronLeft size={18} />
            </button>
            <button
              onClick={() => setCurrentSlide(Math.min(slides.length - 1, currentSlide + 1))}
              disabled={currentSlide === slides.length - 1}
              className="absolute right-1 top-1/2 -translate-y-1/2 w-8 h-8 flex items-center justify-center rounded-full bg-black/40 text-white hover:bg-black/60 disabled:opacity-20 disabled:cursor-not-allowed transition-all"
            >
              <ChevronRight size={18} />
            </button>
          </>
        )}
      </div>

      {/* 页面点指示器 */}
      {slides.length > 1 && (
        <div className="flex justify-center gap-1.5 pb-2">
          {slides.map((_, i) => (
            <button
              key={i}
              onClick={() => setCurrentSlide(i)}
              className={`w-2 h-2 rounded-full transition-all ${
                i === currentSlide ? 'bg-blue-400 w-4' : 'bg-gray-600 hover:bg-gray-500'
              }`}
            />
          ))}
        </div>
      )}

      {/* 演讲者备注 */}
      {slide.speakerNotes && (
        <div className="mx-4 mb-3 p-3 rounded-md bg-gray-900 border border-gray-700">
          <div className="flex items-center gap-1.5 mb-1 text-xs font-semibold text-gray-500">
            🎤 演讲者备注
          </div>
          <p className="text-sm text-gray-400 leading-relaxed whitespace-pre-wrap">{slide.speakerNotes}</p>
        </div>
      )}

      {/* 底栏 */}
      <div className="flex items-center justify-between px-4 py-1.5 bg-gray-900 border-t border-gray-700 text-xs text-gray-600">
        <span>{slides.length} slides</span>
        <span>AI-Generated Presentation</span>
      </div>
    </div>
  );
};

export default JsonPptRenderer;
